import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Settings as SettingsIcon, KeyRound, Save, Loader2, Bell, Palette, Cpu, Check, PlugZap, Briefcase, Shield, Send, HardDrive, Mic, Music, Clapperboard, Gauge, Code2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CinematicStage, StageHeader, GlassCard, Pill, Spinner, cx } from "@/components/shared";

const PROVIDER_KEYS = [
  { id: "openai", label: "OpenAI", hint: "GPT / TTS / image" },
  { id: "anthropic", label: "Anthropic", hint: "Claude models" },
  { id: "gemini", label: "Google Gemini", hint: "Text & image" },
  { id: "elevenlabs", label: "ElevenLabs", hint: "Premium voice" },
  { id: "runway", label: "Runway", hint: "Video generation" },
  { id: "veo", label: "Google Veo", hint: "Video generation" },
];

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [keys, setKeys] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        api_keys: keys, theme: settings.theme, notifications: settings.notifications,
        storage_prefs: settings.storage_prefs,
      });
      toast.success("Settings saved securely (keys encrypted at rest)");
      setKeys({});
      await load();
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const [searchParams] = useSearchParams();
  const [section, setSection] = useState(searchParams.get("section") || "workspace");
  useEffect(() => { const s = searchParams.get("section"); if (s) setSection(s); }, [searchParams]);
  if (!settings) return <Spinner label="Loading Settings…" />;

  const SECTIONS = [
    { id: "workspace", label: "Workspace", icon: Briefcase },
    { id: "providers", label: "Providers", icon: Cpu },
    { id: "security", label: "Security", icon: Shield },
    { id: "brand", label: "Brand", icon: Palette },
    { id: "exports", label: "Exports", icon: Send },
    { id: "storage", label: "Storage", icon: HardDrive },
    { id: "voice", label: "Voice", icon: Mic },
    { id: "music", label: "Music", icon: Music },
    { id: "video", label: "Video", icon: Clapperboard },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "performance", label: "Performance", icon: Gauge },
    { id: "developer", label: "Developer Mode", icon: Code2 },
  ];

  const providers = settings.providers || {};

  return (
    <CinematicStage>
      <StageHeader stage="OPS" room="Studio Operations · Settings" icon={SettingsIcon} title="Studio configuration"
        subtitle="API keys, providers, theme and preferences. Keys are encrypted server-side and never exposed to the client."
        actions={
          <button onClick={save} disabled={saving} data-testid="settings-save-button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#FFB300] text-[#081229] font-semibold glow-gold disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        } />

      <div className="grid lg:grid-cols-4 gap-6">
        <GlassCard className="p-3 self-start lg:sticky lg:top-24">
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => setSection(s.id)} data-testid={`settings-tab-${s.id}`}
              className={cx("w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm mb-0.5 transition",
                section === s.id ? "bg-[#5B4DFF]/20 text-white border border-[#5B4DFF]/40" : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent")}>
              <s.icon className="w-4 h-4" /> {s.label}
            </button>
          ))}
        </GlassCard>

        <div className="lg:col-span-3 space-y-6">
          {section === "providers" && (
            <>
              <GlassCard className="p-6">
                <div className="flex items-center gap-2 mb-5"><KeyRound className="w-5 h-5 text-[#5B4DFF]" /><h3 className="font-display font-semibold">API Key Manager</h3></div>
                <div className="space-y-4">
                  {PROVIDER_KEYS.map((p) => (
                    <div key={p.id} className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="sm:w-40"><div className="text-sm text-white">{p.label}</div><div className="text-xs text-slate-500">{p.hint}</div></div>
                      <div className="flex-1 flex items-center gap-2">
                        <input type="password" placeholder={settings.api_keys?.[p.id] || "Not set"} value={keys[p.id] || ""} onChange={(e) => setKeys((k) => ({ ...k, [p.id]: e.target.value }))} data-testid={`apikey-${p.id}`}
                          className="flex-1 bg-white/5 border border-white/10 focus:border-[#5B4DFF]/60 rounded-xl px-3 py-2.5 outline-none text-white text-sm" />
                        {settings.api_keys?.[p.id] && <Pill tone="green"><Check className="w-3 h-3" /> Set</Pill>}
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>
              <GlassCard className="p-6">
                <div className="flex items-center gap-2 mb-4"><Cpu className="w-5 h-5 text-[#FFB300]" /><h3 className="font-display font-semibold">Active Providers</h3></div>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {Object.entries(providers).map(([cap, p]) => (
                    <div key={cap} className="flex items-center justify-between text-sm rounded-xl bg-white/5 border border-white/10 px-3 py-2">
                      <span className="text-slate-300 capitalize">{cap}</span>
                      <Pill tone={p.configured ? "green" : "slate"}><PlugZap className="w-3 h-3" /> {p.configured ? "Active" : "Standby"}</Pill>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </>
          )}

          {section === "workspace" && (
            <GlassCard className="p-6 space-y-4">
              <div className="flex items-center gap-2"><Briefcase className="w-5 h-5 text-[#5B4DFF]" /><h3 className="font-display font-semibold">Workspace</h3></div>
              <Row icon={Palette} label="Theme"><span className="text-sm text-slate-400">Dark (Cosmic) — default</span></Row>
              <Row icon={Bell} label="Notifications"><Toggle checked={settings.notifications} onChange={(v) => setSettings((s) => ({ ...s, notifications: v }))} testid="settings-notifications" /></Row>
              <Row icon={SettingsIcon} label="Auto-delete media after download"><Toggle checked={settings.storage_prefs?.auto_delete} onChange={(v) => setSettings((s) => ({ ...s, storage_prefs: { ...s.storage_prefs, auto_delete: v } }))} testid="settings-autodelete" /></Row>
              <p className="text-xs text-slate-500 pt-2">Single-owner workspace. Multi-user is supported by the architecture and can be enabled later.</p>
            </GlassCard>
          )}

          {section === "security" && (
            <GlassCard className="p-6 space-y-4">
              <div className="flex items-center gap-2"><Shield className="w-5 h-5 text-[#FFB300]" /><h3 className="font-display font-semibold">Security</h3></div>
              <InfoRow label="Authentication" value="JWT (owner-only)" />
              <InfoRow label="Session timeout" value="12 hours" />
              <InfoRow label="Secrets at rest" value="Fernet-encrypted" />
              <InfoRow label="Brute-force protection" value="5 attempts / 15 min lockout" />
              <p className="text-xs text-slate-500">Provider credentials are stored server-side and never exposed to the client.</p>
            </GlassCard>
          )}

          {section === "developer" && (
            <GlassCard className="p-6 space-y-4">
              <div className="flex items-center gap-2"><Code2 className="w-5 h-5 text-[#5B4DFF]" /><h3 className="font-display font-semibold">Developer Mode</h3></div>
              <Row icon={Code2} label="Verbose pipeline logs"><Toggle checked={settings.storage_prefs?.dev_mode} onChange={(v) => setSettings((s) => ({ ...s, storage_prefs: { ...s.storage_prefs, dev_mode: v } }))} testid="settings-devmode" /></Row>
              <InfoRow label="Provider Registry" value="Clean Architecture — swap via config" />
              <InfoRow label="API base" value="/api" />
            </GlassCard>
          )}

          {section === "performance" && (
            <GlassCard className="p-6 space-y-4">
              <div className="flex items-center gap-2"><Gauge className="w-5 h-5 text-[#FFB300]" /><h3 className="font-display font-semibold">Performance</h3></div>
              <Row icon={Gauge} label="Background processing"><Pill tone="green">On</Pill></Row>
              <Row icon={Gauge} label="Lazy loading & caching"><Pill tone="green">On</Pill></Row>
              <Row icon={Gauge} label="Graceful retry handling"><Pill tone="green">On</Pill></Row>
            </GlassCard>
          )}

          {["brand", "exports", "storage", "voice", "music", "video", "notifications"].includes(section) && (
            <LinkSection section={section} settings={settings} setSettings={setSettings} providers={providers} />
          )}
        </div>
      </div>
    </CinematicStage>
  );
}

function InfoRow({ label, value }) {
  return <div className="flex items-center justify-between text-sm py-1"><span className="text-slate-400">{label}</span><span className="text-white">{value}</span></div>;
}

function LinkSection({ section, settings, setSettings, providers }) {
  const map = {
    brand: { icon: Palette, title: "Brand", desc: "Manage your permanent brand memory in Brand Studio.", to: "/brand-studio" },
    exports: { icon: Send, title: "Exports", desc: "Configure multi-platform exports in the Export Center.", to: "/export-center" },
    storage: { icon: HardDrive, title: "Storage", desc: "Review usage and cleanup rules in Storage.", to: "/storage" },
    voice: { icon: Mic, title: "Voice", desc: "Voice defaults are managed in Voice Studio.", to: "/voice-studio", provider: "voice" },
    music: { icon: Music, title: "Music", desc: "Music defaults apply automatically to every project.", provider: "music" },
    video: { icon: Clapperboard, title: "Video", desc: "Connect a provider in the Video Engine to enable rendering.", to: "/video-engine", provider: "video" },
    notifications: { icon: Bell, title: "Notifications", desc: "Control studio notifications." },
  };
  const c = map[section];
  const Icon = c.icon;
  return (
    <GlassCard className="p-6 space-y-4">
      <div className="flex items-center gap-2"><Icon className="w-5 h-5 text-[#5B4DFF]" /><h3 className="font-display font-semibold">{c.title}</h3></div>
      <p className="text-sm text-slate-400">{c.desc}</p>
      {c.provider && (
        <Row icon={PlugZap} label={`${c.title} provider`}>
          <Pill tone={providers[c.provider]?.configured ? "green" : "slate"}>{providers[c.provider]?.configured ? "Active" : "Standby"}</Pill>
        </Row>
      )}
      {section === "notifications" && (
        <Row icon={Bell} label="Enable notifications"><Toggle checked={settings.notifications} onChange={(v) => setSettings((s) => ({ ...s, notifications: v }))} testid="settings-notif-toggle" /></Row>
      )}
      {c.to && <a href={c.to} className="inline-flex items-center gap-2 text-sm text-[#b6aaff] hover:text-white">Open {c.title} →</a>}
    </GlassCard>
  );
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-sm text-slate-300"><Icon className="w-4 h-4 text-slate-500" /> {label}</span>
      {children}
    </div>
  );
}
function Toggle({ checked, onChange, testid }) {
  return (
    <button onClick={() => onChange(!checked)} data-testid={testid} className={cx("w-11 h-6 rounded-full p-0.5 transition", checked ? "bg-[#5B4DFF]" : "bg-white/10")}>
      <span className={cx("block w-5 h-5 rounded-full bg-white transition-transform", checked && "translate-x-5")} />
    </button>
  );
}
